import { StateCreator } from "zustand";
import { JobStoreUnion } from ".";
import { Logger } from "@/lib/logger/Logger";
import { usePatientStore } from "../Patient";
import { useCentralStore } from "../Central";
import {
	JobEntityType,
	JobWithShare,
	PatientEntityType,
	PractitionerEntityType,
	SupabaseShareEntityViewEnum,
} from "@/lib/supabase/supabaseTypes";
import { produce } from "immer";
import { isLazyPatient } from "../Patient/patients.store";
import { levenshteinDistance } from "../../lib/utils/string-matching";
import { supabase } from "@/lib/supabase";
import { JobStatusEnum } from "@/lib/types/job";
import { fieldIdsForColumns } from "../../pages/job-configuration/field-registry";
import { FieldProblem, FieldProblemSource } from "./field-problems.types";

// Module-level debounce for the inline-autocomplete quick-insert trigger. Any keystroke in a
// patient field while the job has no patient_id schedules a quick-insert 200ms later using the
// current form-field snapshot. Subsequent edits go through normal auto-save.
const QUICK_INSERT_DEBOUNCE_MS = 200;
let quickInsertTimer: ReturnType<typeof setTimeout> | null = null;
let quickInsertInFlight = false;

type AllowedJobFields =
	| "incoming_order_date"
	| "incoming_order_time"
	| "outgoing_order_date"
	| "outgoing_order_time"
	| "description"
	| "tooth_color"
	| "bank_account_id"
	| "tax_rate"
	| "guarantor_id"
	| "tp_value"
	| "prod_country_choice"
	| "prod_country_other"
	| "insurance"
	| "inv_covered_by"
	| "is_ignore_monthly_discount"
	| "is_paid_by_patient";

interface JobFormFields {
	incoming_order_date: string | null;
	incoming_order_time: string | null;
	outgoing_order_date: string | null;
	outgoing_order_time: string | null;
	description: string | null;
	tooth_color: string | null;
	bank_account_id: number | null;
	tax_rate: number | null;
	guarantor_id: string | null;
	tp_value: number | null;
	prod_country_choice: string | null;
	prod_country_other: string | null;
	insurance: string | null;
	inv_covered_by: string | null;
	is_ignore_monthly_discount: boolean | null;
	is_paid_by_patient: boolean | null;
}

const emptyJobFormFields: JobFormFields = {
	incoming_order_date: "",
	incoming_order_time: "",
	outgoing_order_date: "",
	outgoing_order_time: "",
	description: "",
	tooth_color: "",
	bank_account_id: 0,
	tax_rate: 0,
	guarantor_id: "",
	tp_value: 0,
	prod_country_choice: "",
	prod_country_other: "",
	insurance: "",
	inv_covered_by: "",
	is_ignore_monthly_discount: false,
	is_paid_by_patient: false,
};

type AllowedPatientFields =
	| "title"
	| "first_name"
	| "last_name"
	| "code"
	| "birth_date"
	| "street"
	| "city"
	| "postal_code"
	| "country"
	| "additional_information"
	| "gender"
	| "ahv_number";

interface PatientFormFields {
	title: string | null;
	first_name: string | null;
	last_name: string | null;
	code: string | null;
	birth_date: string | null;
	street: string | null;
	city: string | null;
	postal_code: string | null;
	country: string | null;
	additional_information: string | null;
	gender: string | null;
	ahv_number: string | null;
}

const emptyPatientFormFields: PatientFormFields = {
	title: "",
	first_name: "",
	last_name: "",
	code: "",
	birth_date: "",
	street: "",
	city: "",
	postal_code: "",
	country: "",
	additional_information: "",
	gender: "",
	ahv_number: "",
};

export type PatientIdentityField =
	| "first_name"
	| "last_name"
	| "code"
	| "birth_date";

export interface PatientDivergence {
	field: PatientIdentityField;
	originalValue: string;
	typedValue: string;
}

export interface PatientIdentityBaseline {
	patientId: string;
	first_name: string;
	last_name: string;
	code: string;
	birth_date: string;
}

type AllowedPractitionerFields = "title" | "first_name" | "last_name" | "email";

interface PractitionerFormFields {
	title: string | null;
	first_name: string | null;
	last_name: string | null;
	email: string | null;
}

const emptyPractitionerFormFields: PractitionerFormFields = {
	title: "",
	first_name: "",
	last_name: "",
	email: "",
};

interface JobInfoState {
	/**
	 * Input validation: the typed value is invalid and must not be written. Holds the
	 * autosave of its table until the field is edited.
	 */
	formErrors: Record<
		"jobs" | "patients" | "practitioners",
		Record<string, string | null>
	>;

	/**
	 * An action (export, item insert, ...) failed because of this field's value. Shown
	 * like a validation error but never blocks autosave. Keyed by FIELD_REGISTRY id.
	 */
	fieldProblems: Record<
		string,
		FieldProblem & { source: FieldProblemSource }
	>;

	/**
	 * Every field flagged on the current job. Those not placed in the user's tab layout
	 * are rendered in the ad-hoc "Fehlende Angaben" tab until the job changes.
	 */
	adHocFieldIds: string[];

	/**
	 * Field the UI should focus next, as a tab-config field id ("patients.birth_date").
	 * Set by the SV-export preflight so a missing field is not just named in a toast
	 * but actually shown. Cleared by the tabs component once it has focused it.
	 */
	focusFieldId: string | null;

	// Form fields
	formFieldsPatient: PatientFormFields;
	formFieldsJob: JobFormFields;
	formFieldsPractitioner: PractitionerFormFields;

	// Dirty fields are fields that have not been saved yet
	dirtyFieldsPatient: Record<AllowedPatientFields, boolean>;
	dirtyFieldsJob: Record<AllowedJobFields, boolean>;
	dirtyFieldsPractitioner: Record<AllowedPractitionerFields, boolean>;

	// Forms (job, patient or practitioner) with pending changes
	jobPendingSaves: boolean;
	patientPendingSaves: boolean;
	practitionerPendingSaves: boolean;

	// Divergence detection: set when a confirmed patient's identity field edit looks
	// like the user may be targeting a different patient.
	patientDivergence: PatientDivergence | null;
	// Epoch ms until which divergence prompts are suppressed. Set by dismiss so the
	// user isn't re-nagged for subsequent identity-field edits in the same session.
	divergenceSuppressedUntil: number | null;
	// Baseline identity values captured at the moment a patient is loaded. Divergence
	// compares typed values against this baseline rather than `patientsLookup` because
	// auto-save mutates `patientsLookup` before the on-blur evaluation runs.
	patientIdentityBaseline: PatientIdentityBaseline | null;

	// Other in-progress jobs for the currently linked patient (excluding the current job).
	// Non-null and non-empty → suggestion dialog is shown so the user can switch to an
	// existing open job instead of duplicating work on the one they just opened.
	inProgressJobSuggestions: JobWithShare[] | null;
}

export const initialJobFieldErrorState: Pick<
	JobInfoState,
	"formErrors" | "fieldProblems" | "adHocFieldIds" | "focusFieldId"
> = {
	formErrors: {
		jobs: {},
		patients: {},
		practitioners: {},
	},
	fieldProblems: {},
	adHocFieldIds: [],
	focusFieldId: null,
};

export const initialJobInfoState: JobInfoState = {
	...initialJobFieldErrorState,
	formFieldsPatient: emptyPatientFormFields,
	formFieldsJob: emptyJobFormFields,
	formFieldsPractitioner: emptyPractitionerFormFields,
	dirtyFieldsPatient: {} as Record<AllowedPatientFields, boolean>,
	dirtyFieldsJob: {} as Record<AllowedJobFields, boolean>,
	dirtyFieldsPractitioner: {} as Record<AllowedPractitionerFields, boolean>,
	jobPendingSaves: false,
	patientPendingSaves: false,
	practitionerPendingSaves: false,
	patientDivergence: null,
	divergenceSuppressedUntil: null,
	patientIdentityBaseline: null,
	inProgressJobSuggestions: null,
};

const DIVERGENCE_COOLDOWN_MS = 60_000;

export interface JobInfoSlice extends JobInfoState {
	addFormError: (
		table: "jobs" | "patients" | "practitioners",
		field: string,
		error: string | null
	) => void;
	removeFormError: (
		table: "jobs" | "patients" | "practitioners",
		field: string
	) => void;
	requestFieldFocus: (fieldId: string | null) => void;

	/**
	 * Replaces every problem of `source` with `problems` and focuses the first one.
	 * No-op unless `jobId` is the job open on the job page, so callers that also run
	 * elsewhere (e.g. accepting a job request from the job list) stay toast-only.
	 */
	setFieldProblems: (
		jobId: number | null | undefined,
		source: FieldProblemSource,
		problems: FieldProblem[]
	) => void;
	clearFieldProblems: (fieldIds: string[]) => void;
	clearPatientFieldProblems: () => void;
	dismissAdHocFields: () => void;

	// Field update actions
	setJobField: (field: AllowedJobFields, value: any | null) => void;
	setPatientField: (field: AllowedPatientFields, value: any | null) => void;
	setPractitionerField: (
		field: AllowedPractitionerFields,
		value: any | null
	) => void;

	// Batch save actions
	saveJobFields: () => Promise<void>;
	savePatientFields: () => Promise<void>;
	savePractitionerFields: () => Promise<void>;

	// Initialization actions

	// Full initialization for changing jobs
	initializeJobInfoFields: (job: JobEntityType) => Promise<void>;

	initializeJobFormFields: (job: JobEntityType) => void;
	initializePatientFormFields: (patient: PatientEntityType) => void;
	initializePractitionerFormFields: (
		practitioner: PractitionerEntityType
	) => void;

	// Utility actions
	clearDirtyFields: (table?: "jobs" | "patients" | "practitioners") => void;
	resetFormFields: (table?: "jobs" | "patients" | "practitioners") => void;

	// Divergence actions
	evaluatePatientDivergence: () => void;
	dismissPatientDivergence: () => void;
	clearPatientDivergence: () => void;

	// In-progress job suggestions (shown when linking an existing patient who has other open jobs)
	checkInProgressJobsForPatient: (
		patientId: string,
		excludeJobId: number
	) => Promise<void>;
	dismissInProgressJobSuggestions: () => void;
}

export const createJobInfoStore: StateCreator<
	JobStoreUnion,
	[],
	[],
	JobInfoSlice
> = (set, get) => ({
	...initialJobInfoState,
	addFormError: (
		table: "jobs" | "patients" | "practitioners",
		field: string,
		error: string | null
	) => {
		set(
			produce((state) => {
				state.formErrors[table][field] = error;
			})
		);
	},
	removeFormError: (
		table: "jobs" | "patients" | "practitioners",
		field: string
	) => {
		set(
			produce((state) => {
				state.formErrors[table][field] = null;
			})
		);
	},
	requestFieldFocus: (fieldId: string | null) => {
		set(
			produce((state) => {
				state.focusFieldId = fieldId;
			})
		);
	},
	setFieldProblems: (jobId, source, problems) => {
		if (!jobId || get().job?.id !== jobId) return;
		set(
			produce((state: JobInfoState) => {
				for (const [fieldId, problem] of Object.entries(
					state.fieldProblems
				)) {
					if (problem.source === source)
						delete state.fieldProblems[fieldId];
				}
				for (const problem of problems) {
					state.fieldProblems[problem.fieldId] = {
						...problem,
						source,
					};
					if (!state.adHocFieldIds.includes(problem.fieldId)) {
						state.adHocFieldIds.push(problem.fieldId);
					}
				}
				if (problems.length > 0) {
					state.focusFieldId = problems[0].fieldId;
				}
			})
		);
	},
	clearFieldProblems: (fieldIds: string[]) => {
		const { fieldProblems } = get();
		if (!fieldIds.some((fieldId) => fieldProblems[fieldId])) return;
		set(
			produce((state: JobInfoState) => {
				for (const fieldId of fieldIds) {
					delete state.fieldProblems[fieldId];
				}
			})
		);
	},
	clearPatientFieldProblems: () => {
		get().clearFieldProblems(
			Object.keys(get().fieldProblems).filter(
				(fieldId) =>
					fieldId === "patient_select" ||
					fieldId.startsWith("patients.")
			)
		);
	},
	dismissAdHocFields: () => {
		set(
			produce((state: JobInfoState) => {
				state.adHocFieldIds = [];
			})
		);
	},
	// Field update actions
	setJobField: (field: AllowedJobFields, value: any) => {
		set(
			produce((state) => {
				state.formFieldsJob[field] = value;
				state.dirtyFieldsJob[field] = true;
				// A field the user is editing should not stay marked as invalid.
				state.formErrors.jobs[field] = null;
			})
		);
		get().clearFieldProblems(fieldIdsForColumns("jobs", [field]));
	},

	setPatientField: (field: AllowedPatientFields, value: any) => {
		set(
			produce((state) => {
				state.formFieldsPatient[field] = value;
				state.dirtyFieldsPatient[field] = true;
				// A field the user is editing should not stay marked as invalid.
				state.formErrors.patients[field] = null;
			})
		);
		get().clearFieldProblems(fieldIdsForColumns("patients", [field]));
		// Trigger debounced quick-insert when no patient is linked yet.
		const state = get();
		if (!state.job?.patient_id && !quickInsertInFlight) {
			if (quickInsertTimer) clearTimeout(quickInsertTimer);
			quickInsertTimer = setTimeout(async () => {
				quickInsertTimer = null;
				const latest = get();
				if (latest.job?.patient_id) return;
				quickInsertInFlight = true;
				try {
					// Snapshot the values we're sending so we can later clear dirty
					// flags ONLY for fields the user hasn't touched since.
					const snapshot = { ...latest.formFieldsPatient };
					const created = await latest.insertQuickPatient(
						snapshot as Partial<PatientEntityType>
					);
					if (created) {
						set(
							produce((s) => {
								for (const key of Object.keys(
									snapshot
								) as (keyof typeof snapshot)[]) {
									if (
										s.formFieldsPatient[key] ===
										snapshot[key]
									) {
										delete s.dirtyFieldsPatient[key];
									}
								}
							})
						);
					}
				} finally {
					quickInsertInFlight = false;
				}
			}, QUICK_INSERT_DEBOUNCE_MS);
		}
	},

	setPractitionerField: (field: AllowedPractitionerFields, value: any) => {
		set(
			produce((state) => {
				state.formFieldsPractitioner[field] = value;
				state.dirtyFieldsPractitioner[field] = true;
				state.formErrors.practitioners[field] = null;
			})
		);
		get().clearFieldProblems(fieldIdsForColumns("practitioners", [field]));
	},
	// We use updateJobFields from Job store
	saveJobFields: async () => {
		const state = get();
		const { job, updateJobFields } = state;
		if (!job?.id) return;

		const dirtyJobFields = state.dirtyFieldsJob;
		if (Object.keys(dirtyJobFields).length === 0) return;

		set(
			produce((state) => {
				state.jobPendingSaves = true;
			})
		);

		try {
			// Save each dirty field that doesn't have an error
			const toUpdate: Partial<JobEntityType> = Object.keys(
				dirtyJobFields
			).reduce((acc, field) => {
				if (state.formErrors.jobs[field]) {
					Logger.log(
						`Skipping field ${field} due to validation error`
					);
					return acc;
				}
				return {
					...acc,
					[field]: state.formFieldsJob[field as AllowedJobFields],
				};
			}, {});

			if (Object.keys(toUpdate).length > 0) {
				await updateJobFields(toUpdate);
			}

			// Fields skipped for a validation error stay dirty so the correction is saved later.
			set(
				produce((state) => {
					for (const field of Object.keys(toUpdate)) {
						delete state.dirtyFieldsJob[field];
					}
					state.jobPendingSaves = false;
				})
			);
		} catch (error) {
			Logger.error("Error saving job fields: " + error);
			set(
				produce((state) => {
					state.jobPendingSaves = false;
				})
			);
		}
	},

	// We use upsertPatient from Patient store
	savePatientFields: async () => {
		const state = get();
		const { job } = state;
		if (!job?.patient_id) return;

		const dirtyPatientFields = state.dirtyFieldsPatient;
		if (Object.keys(dirtyPatientFields).length === 0) return;

		// Gate the save behind divergence detection. If the user's identity-field edits
		// look like a patient switch, hold the save and let the dialog ask them first.
		get().evaluatePatientDivergence();
		if (get().patientDivergence) return;

		const patientStore = usePatientStore.getState();
		const currentPatient = patientStore.patientsLookup[job.patient_id];
		if (!currentPatient) return;

		set(
			produce((state) => {
				state.patientPendingSaves = true;
			})
		);

		try {
			// Build updated patient object with only non-error fields and filter out search_vector
			const updates: any = { ...currentPatient };
			delete updates.search_vector;

			const savedFields: string[] = [];

			Object.keys(dirtyPatientFields).forEach((field) => {
				if (!state.formErrors.patients[field]) {
					let value =
						state.formFieldsPatient[field as AllowedPatientFields];
					if (value !== undefined) {
						// Convert empty strings to null for date fields
						if (field === "birth_date" && value === "") {
							value = null;
						}
						updates[field] = value;
						savedFields.push(field);
					}
				} else {
					Logger.log(
						`Skipping patient field ${field} due to validation error`
					);
				}
			});

			if (savedFields.length > 0) {
				await patientStore.upsertPatient(updates, true);
			}

			// Fields skipped for a validation error stay dirty so the correction is saved later.
			set(
				produce((state) => {
					for (const field of savedFields) {
						delete state.dirtyFieldsPatient[field];
					}
					state.patientPendingSaves = false;
				})
			);

			// If this is a quick-insert patient and names are now complete,
			// regenerate the placeholder code (frontend-only, keeps local state in sync).
			await (get() as any).maybeRegenerateQuickInsertCode?.();
		} catch (error) {
			Logger.error("Error saving patient fields: " + error);
			set(
				produce((state) => {
					state.patientPendingSaves = false;
				})
			);
		}
	},

	// We use updatePractitioner from Central store
	savePractitionerFields: async () => {
		const state = get();
		const { job } = state;
		if (!job?.practitioner_id) return;

		const dirtyPractitionerFields = state.dirtyFieldsPractitioner;
		if (Object.keys(dirtyPractitionerFields).length === 0) return;

		const currentPractitioner =
			useCentralStore.getState().practitionersLookup[job.practitioner_id];
		if (!currentPractitioner) return;

		set(
			produce((state) => {
				state.practitionerPendingSaves = true;
			})
		);

		try {
			// Build updated practitioner object with only non-error fields
			const updates: any = { ...currentPractitioner };
			const savedFields: string[] = [];

			Object.keys(dirtyPractitionerFields).forEach((field) => {
				if (!state.formErrors.practitioners[field]) {
					const value =
						state.formFieldsPractitioner[
							field as AllowedPractitionerFields
						];
					if (value !== undefined) {
						updates[field] = value;
						savedFields.push(field);
					}
				} else {
					Logger.log(
						`Skipping practitioner field ${field} due to validation error`
					);
				}
			});

			if (savedFields.length > 0) {
				await useCentralStore.getState().updatePractitioner(updates);
			}

			// Fields skipped for a validation error stay dirty so the correction is saved later.
			set(
				produce((state) => {
					for (const field of savedFields) {
						delete state.dirtyFieldsPractitioner[field];
					}
					state.practitionerPendingSaves = false;
				})
			);
		} catch (error) {
			Logger.error("Error saving practitioner fields: " + error);
			set(
				produce((state) => {
					state.practitionerPendingSaves = false;
				})
			);
		}
	},
	clearDirtyFields: (table?: "jobs" | "patients" | "practitioners") => {
		if (table === "jobs") {
			set(
				produce((state) => {
					state.dirtyFieldsJob = {};
				})
			);
		} else if (table === "patients") {
			set(
				produce((state) => {
					state.dirtyFieldsPatient = {};
				})
			);
		} else if (table === "practitioners") {
			set(
				produce((state) => {
					state.dirtyFieldsPractitioner = {};
				})
			);
		} else {
			set(
				produce((state) => {
					state.dirtyFieldsJob = {};
					state.dirtyFieldsPatient = {};
					state.dirtyFieldsPractitioner = {};
				})
			);
		}
	},
	resetFormFields: (table?: "jobs" | "patients" | "practitioners") => {
		if (table === "jobs" || !table) {
			set(
				produce((state) => {
					state.formFieldsJob = emptyJobFormFields;
					state.dirtyFieldsJob = {};
				})
			);
		}
		if (table === "patients" || !table) {
			set(
				produce((state) => {
					state.formFieldsPatient = emptyPatientFormFields;
					state.dirtyFieldsPatient = {};
				})
			);
		}
		if (table === "practitioners" || !table) {
			set(
				produce((state) => {
					state.formFieldsPractitioner = emptyPractitionerFormFields;
					state.dirtyFieldsPractitioner = {};
				})
			);
		}
	},
	evaluatePatientDivergence: () => {
		const state = get() as any;
		const job = state.job;
		const patientId = job?.patient_id;
		const quickInsertId = state.quickInsertPatientId;
		// Only evaluate for confirmed patients (not quick-insert, not empty).
		if (!patientId || quickInsertId === patientId) {
			if (state.patientDivergence) {
				set(
					produce((s) => {
						s.patientDivergence = null;
					})
				);
			}
			return;
		}
		// Org-level config: allow disabling the warning, raising the tolerance,
		// or excluding the code field (which users often rewrite from the auto-generated value).
		const orgConfig = useCentralStore.getState().organization?.config as
			| Record<string, unknown>
			| null
			| undefined;
		const divergenceEnabled =
			orgConfig?.patient_divergence_enabled !== false;
		if (!divergenceEnabled) {
			if (state.patientDivergence) {
				set(
					produce((s) => {
						s.patientDivergence = null;
					})
				);
			}
			return;
		}
		const threshold = Number(orgConfig?.patient_divergence_threshold) || 2;
		const ignoreCode = orgConfig?.patient_divergence_ignore_code === true;
		// Cooldown: user recently dismissed a divergence — don't re-nag for the window.
		const suppressedUntil = state.divergenceSuppressedUntil as
			| number
			| null;
		if (suppressedUntil && Date.now() < suppressedUntil) {
			if (state.patientDivergence) {
				set(
					produce((s) => {
						s.patientDivergence = null;
					})
				);
			}
			return;
		}
		// Compare against the baseline captured at patient-load time, NOT against
		// patientsLookup — auto-save updates patientsLookup to the typed value before
		// this blur handler runs, which would hide the divergence.
		const baseline =
			state.patientIdentityBaseline as PatientIdentityBaseline | null;
		if (!baseline || baseline.patientId !== patientId) return;

		const form = state.formFieldsPatient;
		const fields: PatientIdentityField[] = ignoreCode
			? ["first_name", "last_name", "birth_date"]
			: ["first_name", "last_name", "code", "birth_date"];

		let found: PatientDivergence | null = null;
		for (const f of fields) {
			const orig = baseline[f] ?? "";
			const typed = (form[f] ?? "").toString().trim();
			if (!orig || !typed || orig === typed) continue;
			if (levenshteinDistance(orig, typed) > threshold) {
				found = { field: f, originalValue: orig, typedValue: typed };
				break;
			}
		}

		if (!found) {
			if (state.patientDivergence) {
				set(
					produce((s) => {
						s.patientDivergence = null;
					})
				);
			}
			return;
		}

		set(
			produce((s) => {
				s.patientDivergence = found;
			})
		);
	},
	dismissPatientDivergence: () => {
		const current = (get() as any)
			.patientDivergence as PatientDivergence | null;
		if (!current) return;
		set(
			produce((s) => {
				s.patientDivergence = null;
				s.divergenceSuppressedUntil =
					Date.now() + DIVERGENCE_COOLDOWN_MS;
			})
		);
		// Retry the save that was held back by the divergence check.
		get().savePatientFields();
	},
	clearPatientDivergence: () => {
		set(
			produce((s) => {
				s.patientDivergence = null;
				s.divergenceSuppressedUntil = null;
			})
		);
	},
	checkInProgressJobsForPatient: async (
		patientId: string,
		excludeJobId: number
	) => {
		const { data, error } = await supabase
			.from(SupabaseShareEntityViewEnum.JOBS_WITH_SHARES)
			.select("*")
			.eq("patient_id", patientId)
			.eq("status", JobStatusEnum.IN_PROGRESS)
			.neq("id", excludeJobId)
			.order("created_at", { ascending: false });
		if (error) {
			Logger.error(
				error,
				{},
				"Error fetching in-progress jobs for patient"
			);
			return;
		}
		if (!data || data.length === 0) return;
		set(
			produce((s) => {
				s.inProgressJobSuggestions = data;
			})
		);
	},
	dismissInProgressJobSuggestions: () => {
		set(
			produce((s) => {
				s.inProgressJobSuggestions = null;
			})
		);
	},
	initializeJobInfoFields: async (job: JobEntityType) => {
		// If a job is initialized, we can and should always clear the dirty fields of whatever was set before
		set(
			produce((state) => {
				state.dirtyFieldsJob = {};
				state.dirtyFieldsPatient = {};
				state.dirtyFieldsPractitioner = {};
				state.inProgressJobSuggestions = null;
			})
		);

		get().initializeJobFormFields(job);

		// Initialize patient form fields if patient exists
		// Or reset the form fields if no patient exists
		if (job.patient_id) {
			const patientStore = usePatientStore.getState();
			const patient = patientStore.patientsLookup[job.patient_id];
			if (patient && !isLazyPatient(patient)) {
				get().initializePatientFormFields(patient as PatientEntityType);
			} else {
				const fetchedPatient = await patientStore.getPatient(
					job.patient_id
				);
				if (fetchedPatient) {
					get().initializePatientFormFields(
						fetchedPatient as PatientEntityType
					);
				}
			}
		} else {
			set(
				produce((state) => {
					state.formFieldsPatient = emptyPatientFormFields;
					state.patientIdentityBaseline = null;
					state.patientDivergence = null;
					state.divergenceSuppressedUntil = null;
				})
			);
		}

		// Initialize practitioner form fields if practitioner exists
		// Or reset the form fields if no practitioner exists
		if (job.practitioner_id) {
			const centralStore = useCentralStore.getState();
			const practitioner =
				centralStore.practitionersLookup[job.practitioner_id];
			if (practitioner) {
				get().initializePractitionerFormFields(
					practitioner as PractitionerEntityType
				);
			}
		} else {
			set(
				produce((state) => {
					state.formFieldsPractitioner = emptyPractitionerFormFields;
				})
			);
		}
	},
	initializeJobFormFields: (job: JobEntityType) => {
		// For type-safety, we convert the job object to the JobFormFields type
		const jobFormFields: JobFormFields = {
			incoming_order_date: job.incoming_order_date,
			incoming_order_time: job.incoming_order_time,
			outgoing_order_date: job.outgoing_order_date,
			outgoing_order_time: job.outgoing_order_time,
			description: job.description,
			tooth_color: job.tooth_color,
			bank_account_id: job.bank_account_id,
			tax_rate: job.tax_rate,
			guarantor_id: job.guarantor_id,
			tp_value: job.tp_value,
			prod_country_choice: job.prod_country_choice,
			prod_country_other: job.prod_country_other,
			insurance: job.insurance,
			inv_covered_by: job.inv_covered_by,
			is_ignore_monthly_discount: job.is_ignore_monthly_discount,
			is_paid_by_patient: job.is_paid_by_patient,
		};
		set(
			produce((state) => {
				state.formFieldsJob = jobFormFields;
			})
		);
	},
	initializePatientFormFields: (patient: PatientEntityType) => {
		// For type-safety, we convert the patient object to the PatientFormFields type
		const patientFormFields: PatientFormFields = {
			title: patient.title,
			first_name: patient.first_name,
			last_name: patient.last_name,
			code: patient.code,
			birth_date: patient.birth_date,
			street: patient.street,
			city: patient.city,
			postal_code: patient.postal_code,
			country: patient.country,
			additional_information: patient.additional_information,
			gender: patient.gender,
			ahv_number: patient.ahv_number,
		};
		const baseline: PatientIdentityBaseline | null = patient.id
			? {
					patientId: patient.id,
					first_name: (patient.first_name ?? "").trim(),
					last_name: (patient.last_name ?? "").trim(),
					code: (patient.code ?? "").trim(),
					birth_date: (patient.birth_date ?? "").trim(),
				}
			: null;
		set(
			produce((state) => {
				state.formFieldsPatient = patientFormFields;
				state.patientIdentityBaseline = baseline;
				state.patientDivergence = null;
				state.divergenceSuppressedUntil = null;
			})
		);
	},
	initializePractitionerFormFields: (
		practitioner: PractitionerEntityType
	) => {
		// For type-safety, we convert the practitioner object to the PractitionerFormFields type
		const practitionerFormFields: PractitionerFormFields = {
			title: practitioner.title,
			first_name: practitioner.first_name,
			last_name: practitioner.last_name,
			email: practitioner.email,
		};
		set(
			produce((state) => {
				state.formFieldsPractitioner = practitionerFormFields;
			})
		);
	},
});
